// FieldBuilderTool.jsx — root container for the AI Form Builder (internal
// codename: Custom Field Builder; docs/plans/custom-field-builder.md).
//
// Milestone 2 scope: the M1 pipeline (sessions list → upload → stage-1
// structural read → scope confirmation → stage-2 mapping) now lands in the
// rendered, editable FORM BUILDER: controls per type, inline editing, type/
// fieldset changes with re-gating, add/delete/reorder, buckets with dismiss
// and promote, the needs-attention strip, the page-level source panel, and
// debounced AUTOSAVE with resume. Still NO execute path — the riskiest thing
// this tool can do to FMX is a GET. The sequential POST engine + ledger +
// rollback arrive in Milestone 3.
//
// Owns all data: the session row (status drives routing), the immutable
// extraction snapshots, the draft (edits flow through here so autosave and
// validation stay in one place), and the live FMX context (post-options,
// existing-field sweep, native labels) that validation runs against.

import { useEffect, useMemo, useRef, useState } from 'react';
import { T } from '../../theme';
import {
  listFieldBuilderSessions, createFieldBuilderSession, updateFieldBuilderSession,
  deleteFieldBuilderSession, createFieldBuilderExtraction,
  getLatestFieldBuilderExtraction, completeFieldBuilderExtractionStage2,
  getAllDependencyCaches,
} from '../../db';
import {
  prepareSource, uploadSourceToStorage, runStage1, runStage2, suggestFieldsets,
  loadStoredSource,
} from '../../fieldBuilder/extraction';
import { fetchCustomFieldsPostOptions, listCustomFields } from '../../fieldBuilder/fmxCustomFieldsApi';
import { FIELDSET_ENTITY_SCHEMA } from '../../fieldBuilder/nativeFields';
import { draftFromStage2 } from '../../fieldBuilder/extractionShapes';
import { applySectionScoping } from '../../fieldBuilder/mergeFields';
import { validateDraftBatch } from '../../fieldBuilder/validateFieldDraft';
import { buildAllPostBodies } from '../../fieldBuilder/postBody';
import { fetchPostOptions, normalizeModules } from '../../fmxSync';
import SessionsList from './SessionsList';
import UploadStep from './UploadStep';
import ExtractionProgress from './ExtractionProgress';
import ScopeStep from './ScopeStep';
import FormBuilder from './FormBuilder';
import DryRunStep from './DryRunStep';

const AUTOSAVE_DEBOUNCE_MS = 700;

// Live system-field labels of each fieldset's TARGET entity form (the work
// request form, the equipment form, …) for the layer-2 native screen.
// Module-scoped entities need a module slug; we match the scope screen's
// module pick to project.fmx_modules by label, falling back to the first
// module. Best-effort by design — the static registry is the base layer.
async function fetchNativeLabels(project, fieldsets, scope) {
  const out = {};
  const modules = normalizeModules(project?.fmx_modules);
  const moduleListFor = { 'Work Request': 'workRequestModules', 'Schedule Request': 'scheduleRequestModules', 'Work Task': 'workTaskModules' };
  const moduleScopeKey = { 'Work Request': 'workRequestModuleID', 'Work Task': 'workTaskModuleID' };
  for (const fieldset of fieldsets) {
    const base = FIELDSET_ENTITY_SCHEMA[fieldset];
    if (!base) continue;
    let schemaType = base;
    const listKey = moduleListFor[base];
    if (listKey) {
      const list = modules[listKey] || [];
      const picked = scope?.scoping?.[fieldset]?.[moduleScopeKey[base]];
      const pickedName = picked && typeof picked === 'object' ? picked.name : null;
      const slug = (pickedName && list.find(m => m.label === pickedName)?.slug) || list[0]?.slug;
      if (!slug) continue;
      schemaType = `${base}:${slug}`;
    }
    try {
      const { systemFields } = await fetchPostOptions({ projectId: project.id }, schemaType, project?.fmx_modules);
      const labels = (systemFields || []).map(sf => sf.label).filter(Boolean);
      if (labels.length) out[fieldset] = labels;
    } catch { /* best-effort — static registry still applies */ }
  }
  return out;
}

export default function FieldBuilderTool({ project, currentUser }) {
  const projectId = project?.id;
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [view, setView] = useState('list');           // 'list' | 'upload' | 'session'
  const [subview, setSubview] = useState('builder');  // within drafting: 'builder' | 'dryrun'
  const [session, setSession] = useState(null);
  const [extraction, setExtraction] = useState(null); // latest snapshot row
  const [progress, setProgress] = useState(null);     // { title, label, current, total }
  const [errorMsg, setErrorMsg] = useState(null);
  // Live FMX context validation runs against. Fetched at scope confirmation
  // and RE-FETCHED fresh on dry-run entry — a draft can sit for days and the
  // site can change underneath it.
  const [fmxCtx, setFmxCtx] = useState({ postOptionsByFieldset: {}, existingFieldsByFieldset: {}, nativeLabelsByFieldset: {} });
  // The source panel's document: fresh sessions keep the just-prepared pages
  // in memory; resumed sessions re-materialize the stored original on demand.
  // The ref guards against a double download while the panel is still idle.
  const [sourceDoc, setSourceDoc] = useState({ status: 'idle' });
  const sourceLoadingRef = useRef(false);
  // Site users from the dependency cache — feeds the user-picker control so
  // it feels real (the selection means nothing).
  const [siteUsers, setSiteUsers] = useState([]);

  const reloadSessions = async () => {
    setLoadingSessions(true);
    setSessions(await listFieldBuilderSessions(projectId));
    setLoadingSessions(false);
  };
  useEffect(() => {
    if (!projectId) return;
    setView('list'); setSession(null); setExtraction(null); setErrorMsg(null);
    sourceLoadingRef.current = false;
    setSourceDoc({ status: 'idle' });
    reloadSessions();
    getAllDependencyCaches(projectId).then(rows => {
      const users = rows.find(r => r.schema_type === 'users')?.extra?.items || [];
      setSiteUsers(users.map(u => u?.name || u?.email).filter(Boolean));
    });
  }, [projectId]); // dep: project switch is the only reset trigger

  const stage1 = extraction?.stage1_json?.stage1 || null;
  const stage2 = extraction?.stage2_json?.stage2 || null;
  const draft = session?.draft || null;
  const scope = session?.scope || null;
  const confirmedFieldsets = scope?.fieldsets || [];

  // ── Autosave: every edit debounce-saves the draft (§6) ──────────────────
  // Refs keep the flush callable from timers and unmount cleanup without
  // stale closures. db.js swallows errors (returns null), so a failed write
  // surfaces as saveState 'error' and the pending draft is retained for the
  // next flush.
  const [saveState, setSaveState] = useState('saved'); // 'saved' | 'saving' | 'error'
  const pendingDraftRef = useRef(null);
  const saveTimerRef = useRef(null);
  const sessionIdRef = useRef(null);
  sessionIdRef.current = session?.id || null;

  const flushDraftSave = async () => {
    clearTimeout(saveTimerRef.current);
    const draftToSave = pendingDraftRef.current;
    if (draftToSave == null || !sessionIdRef.current) return true;
    pendingDraftRef.current = null;
    const updated = await updateFieldBuilderSession(sessionIdRef.current, { draft: draftToSave });
    if (!updated) {
      // Keep the unsaved draft unless an even newer edit superseded it.
      if (pendingDraftRef.current == null) pendingDraftRef.current = draftToSave;
      setSaveState('error');
      return false;
    }
    if (pendingDraftRef.current == null) setSaveState('saved');
    return true;
  };
  const flushRef = useRef(flushDraftSave);
  flushRef.current = flushDraftSave;
  useEffect(() => () => { flushRef.current(); }, []); // best-effort flush on unmount

  const handleDraftChange = (nextDraft) => {
    setSession(s => (s ? { ...s, draft: nextDraft } : s));
    pendingDraftRef.current = nextDraft;
    setSaveState('saving');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { flushRef.current(); }, AUTOSAVE_DEBOUNCE_MS);
  };

  const loadFmxCtx = async (fieldsets, scopeSel, presetPostOptions = null) => {
    const postOptionsByFieldset = { ...(presetPostOptions || {}) };
    const existingFieldsByFieldset = {};
    for (const fs of fieldsets) {
      if (!postOptionsByFieldset[fs]) {
        postOptionsByFieldset[fs] = await fetchCustomFieldsPostOptions(projectId, fs);
      }
      existingFieldsByFieldset[fs] = await listCustomFields(projectId, { fieldset: fs });
    }
    const nativeLabelsByFieldset = await fetchNativeLabels(project, fieldsets, scopeSel);
    const ctx = { postOptionsByFieldset, existingFieldsByFieldset, nativeLabelsByFieldset };
    setFmxCtx(ctx);
    return ctx;
  };

  // ── Source panel loading ──────────────────────────────────────────────────
  // Fresh sessions set the doc directly from the prepared source; resumed
  // sessions call this lazily the first time the panel opens.
  const loadSource = async () => {
    if (sourceLoadingRef.current) return;
    sourceLoadingRef.current = true;
    setSourceDoc({ status: 'loading' });
    try {
      const doc = await loadStoredSource(session);
      setSourceDoc({ status: 'ready', ...doc });
    } catch (err) {
      setSourceDoc({ status: 'error', error: err?.message || String(err) });
      sourceLoadingRef.current = false; // a later visit may retry
    }
  };

  const sourceDocFromPrepared = (source) => {
    if (source.images) {
      return {
        status: 'ready', kind: source.kind,
        pages: source.images.map(img => `data:${img.mediaType};base64,${img.base64}`),
      };
    }
    return { status: 'ready', kind: source.kind, text: source.text };
  };

  // ── New session: upload → stage 1 ────────────────────────────────────────
  const handleSource = async ({ file, pastedText }) => {
    setErrorMsg(null);
    let row = null;
    try {
      setProgress({ title: 'Reading the document', label: 'Preparing…' });
      const source = await prepareSource({
        file, pastedText,
        onProgress: (label, p) => setProgress({ title: 'Reading the document', label, ...p }),
      });
      setSourceDoc(sourceDocFromPrepared(source));
      const storageKey = await uploadSourceToStorage({ file, pastedText, userId: currentUser?.id });
      row = await createFieldBuilderSession({
        projectId,
        createdBy: currentUser?.id,
        sourceKind: source.kind,
        sourceFilename: source.filename,
        storageKey,
        pageCount: source.pageCount,
      });
      if (!row) throw new Error('Could not create the builder session — check your connection and retry.');
      setSession(row); setView('session');

      const s1 = await runStage1({
        projectId, userId: currentUser?.id, source, storageKey,
        onProgress: (label, p) => setProgress({ title: 'Reading the document', label, ...p }),
      });
      const snap = await createFieldBuilderExtraction({
        sessionId: row.id, projectId, version: 1,
        stage1Json: { stage1: s1.stage1, quarantined: s1.quarantined },
        stage1RunId: s1.runId,
      });
      setExtraction(snap);
      // A read that produced zero items has nothing to scope or map — stop
      // here with the model's own explanation instead of a "0 items" scope
      // screen. (The snapshot is kept for admin inspection.)
      const itemCount = s1.stage1.sections.reduce((n, sec) => n + (sec.items?.length || 0), 0);
      if (itemCount === 0) {
        throw new Error(
          'The document was read successfully, but no form items were found.'
          + (s1.stage1.note ? ` The AI noted: “${s1.stage1.note}”.` : '')
          + ' The reader handles fillable forms AND written form specifications — if this document is one of those, retry in a new session; if extraction keeps missing it, check the “structure” prompt in Admin → Prompts.'
        );
      }
      const updated = await updateFieldBuilderSession(row.id, { status: 'scoping' });
      setSession(updated || { ...row, status: 'scoping' });
      setProgress(null);
      reloadSessions();
    } catch (err) {
      setProgress(null);
      const message = err?.message || String(err);
      if (row?.id) {
        const updated = await updateFieldBuilderSession(row.id, { status: 'error', error: message });
        setSession(updated || { ...row, status: 'error', error: message });
        reloadSessions();
      } else {
        setErrorMsg(message);
        setView('upload');
      }
    }
  };

  // ── Scope confirmed → stage 2 → draft ────────────────────────────────────
  const handleScopeConfirm = async (scopeSel, postOptionsByFieldset) => {
    setErrorMsg(null);
    try {
      setProgress({ title: 'Mapping to FMX', label: 'Checking existing fields on the site…' });
      const ctx = await loadFmxCtx(scopeSel.fieldsets, scopeSel, postOptionsByFieldset);
      const s2 = await runStage2({
        projectId, userId: currentUser?.id,
        stage1,
        confirmedFieldsets: scopeSel.fieldsets,
        nativeLabelsByFieldset: ctx.nativeLabelsByFieldset,
        sourceFilename: session?.source_filename,
        onProgress: (label, p) => setProgress({ title: 'Mapping to FMX', label, ...p }),
      });
      const snap = await completeFieldBuilderExtractionStage2(
        extraction.id, { stage2: s2.stage2, quarantined: s2.quarantined }, s2.runId
      );
      if (snap) setExtraction(snap);

      const newDraft = draftFromStage2(s2.stage2, stage1);
      // Rule R2: a merged field is created scoped to exactly the request types
      // whose sections asked it, from the mapping confirmed on the scope screen.
      if (scopeSel.sectionRequestTypes && Object.keys(scopeSel.sectionRequestTypes).length) {
        newDraft.fields = applySectionScoping(newDraft.fields, scopeSel.sectionRequestTypes);
      }
      newDraft.buckets = {
        nativeMappings: s2.stage2.nativeMappings,
        prerequisites: s2.stage2.prerequisites,
        unsupportedLogic: s2.stage2.unsupportedLogic,
        flags: s2.stage2.flags,
      };
      newDraft.quarantined = [
        ...(extraction?.stage1_json?.quarantined || []).map(q => ({ ...q, stage: 1 })),
        ...s2.quarantined.map(q => ({ ...q, stage: 2 })),
      ];
      const updated = await updateFieldBuilderSession(session.id, {
        scope: scopeSel, draft: newDraft, status: 'drafting',
      });
      setSession(updated || { ...session, scope: scopeSel, draft: newDraft, status: 'drafting' });
      setSubview('builder');
      setProgress(null);
      reloadSessions();
    } catch (err) {
      setProgress(null);
      setErrorMsg(err?.message || String(err));
    }
  };

  // ── Resume from the sessions list ────────────────────────────────────────
  const handleResume = async (row) => {
    setErrorMsg(null);
    setSession(row);
    setSubview('builder');
    setView('session');
    setSaveState('saved');
    pendingDraftRef.current = null;
    sourceLoadingRef.current = false;
    setSourceDoc({ status: 'idle' }); // lazy — loads when the panel first opens
    const snap = await getLatestFieldBuilderExtraction(row.id);
    setExtraction(snap);
    if (row.status === 'scoping' && !snap?.stage1_json?.stage1) {
      setErrorMsg('Could not load this session’s extraction snapshot — check your connection and re-open it from the list.');
    }
    if (row.status === 'drafting' && row.scope?.fieldsets?.length) {
      // Rebuild the live context in the background so validation badges are
      // current; the builder renders immediately from the saved draft.
      loadFmxCtx(row.scope.fieldsets, row.scope).catch(() => {});
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete this builder session (${row.source_filename || row.source_kind})? The original document stays in storage history; nothing on FMX is affected.`)) return;
    await deleteFieldBuilderSession(row.id);
    if (session?.id === row.id) { setSession(null); setView('list'); }
    reloadSessions();
  };

  // ── Dry-run entry: flush autosave, re-fetch live context, then gate ──────
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const enterDryRun = async () => {
    setDryRunLoading(true);
    setErrorMsg(null);
    try {
      const saved = await flushDraftSave();
      if (!saved) throw new Error('Could not save the draft — check your connection and try again. The dry run must reflect what is stored.');
      await loadFmxCtx(confirmedFieldsets, scope); // fresh, per §5 of the plan
      setSubview('dryrun');
    } catch (err) {
      setErrorMsg(`Could not open the dry run: ${err?.message || err}`);
    }
    setDryRunLoading(false);
  };

  // Validation + POST bodies, recomputed whenever the draft or live context
  // moves. The same pure function gates the builder badges and the dry run.
  const validation = useMemo(() => {
    if (!draft?.fields?.length) return new Map();
    return validateDraftBatch(draft.fields, {
      confirmedFieldsets,
      postOptionsByFieldset: fmxCtx.postOptionsByFieldset,
      scope: scope?.scoping || {},
      existingFieldsByFieldset: fmxCtx.existingFieldsByFieldset,
      nativeLabelsByFieldset: fmxCtx.nativeLabelsByFieldset,
    });
  }, [draft, confirmedFieldsets, fmxCtx, scope]);

  const bodies = useMemo(() => {
    if (!draft?.fields?.length) return [];
    return buildAllPostBodies(draft.fields, scope?.scoping || {}, confirmedFieldsets);
  }, [draft, scope, confirmedFieldsets]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!projectId) return null;

  const backToList = () => {
    flushRef.current();
    setSession(null); setExtraction(null); setView('list'); setErrorMsg(null);
    sourceLoadingRef.current = false;
    setSourceDoc({ status: 'idle' });
    reloadSessions();
  };

  const errorBanner = errorMsg && (
    <div style={{ margin: '0 0 12px', padding: '10px 14px', background: T.tint.error, border: `1px solid ${T.badge.red}`, borderRadius: T.radiusCard, font: `500 13px/18px ${T.fontSans}`, color: T.redDarker }}>
      {errorMsg}
    </div>
  );

  if (view === 'list') {
    return (
      <div>
        {errorBanner}
        <SessionsList
          sessions={sessions}
          loading={loadingSessions}
          siteUrl={project?.fmx_site_url}
          hasCreds={!!project?.fmx_credentials}
          onNew={() => { setErrorMsg(null); setView('upload'); }}
          onResume={handleResume}
          onDelete={handleDelete}
        />
      </div>
    );
  }

  if (view === 'upload') {
    return (
      <div>
        {errorBanner}
        {progress
          ? <ExtractionProgress {...progress} />
          : <UploadStep onSource={handleSource} onBack={backToList} />}
      </div>
    );
  }

  // view === 'session'
  const status = session?.status;
  return (
    <div>
      {errorBanner}
      {progress && <ExtractionProgress {...progress} />}

      {!progress && (status === 'error' || status === 'extracting') && (
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
          <div style={{ font: `700 16px/22px ${T.fontSans}`, color: T.ink, marginBottom: 6 }}>
            Extraction {status === 'error' ? 'failed' : 'was interrupted'} — nothing was created
          </div>
          <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginBottom: 14 }}>
            {session?.error || 'The session did not finish reading the document. Start a new session from the same document to retry.'}
          </div>
          <button className="fmx-btn-secondary" onClick={backToList}>Back to sessions</button>
        </div>
      )}

      {!progress && status === 'scoping' && stage1 && (
        <ScopeStep
          project={project}
          session={session}
          stage1={stage1}
          suggestions={suggestFieldsets(stage1)}
          fetchPostOptionsForFieldset={(fs) => fetchCustomFieldsPostOptions(projectId, fs)}
          onConfirm={handleScopeConfirm}
          onBack={backToList}
        />
      )}

      {!progress && status === 'drafting' && draft && subview === 'builder' && (
        <FormBuilder
          session={session}
          draft={draft}
          stage1={stage1}
          stage2={stage2}
          validation={validation}
          siteUrl={project?.fmx_site_url}
          siteUsers={siteUsers}
          confirmedFieldsets={confirmedFieldsets}
          existingFieldsByFieldset={fmxCtx.existingFieldsByFieldset}
          postOptionsByFieldset={fmxCtx.postOptionsByFieldset}
          sourceDoc={sourceDoc}
          onLoadSource={loadSource}
          saveState={saveState}
          onDraftChange={handleDraftChange}
          onContinue={enterDryRun}
          continueLoading={dryRunLoading}
          onBack={backToList}
        />
      )}

      {!progress && status === 'drafting' && draft && subview === 'dryrun' && (
        <DryRunStep
          session={session}
          draft={draft}
          bodies={bodies}
          validation={validation}
          siteUrl={project?.fmx_site_url}
          onBack={() => setSubview('builder')}
        />
      )}
    </div>
  );
}
